import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Flame, Check, Instagram, AlertTriangle, Copy, AlertCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useOrder } from "@/context/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fortnightly payment-due anchor: 24 June 2026, then +14 days each cycle.
function getNextPaymentDue() {
  const anchor = new Date(2026, 5, 24); // June (0-indexed)
  anchor.setHours(23, 59, 59, 999);
  const now = new Date();
  let due = new Date(anchor);
  while (due.getTime() < now.getTime()) {
    due = new Date(due.getTime() + 14 * 24 * 60 * 60 * 1000);
  }
  return due;
}

function formatPaymentDue(d) {
  const day = d.getDate();
  const rem10 = day % 10;
  const rem100 = day % 100;
  const suffix =
    rem100 >= 11 && rem100 <= 13 ? "th" :
    rem10 === 1 ? "st" :
    rem10 === 2 ? "nd" :
    rem10 === 3 ? "rd" : "th";
  const weekday = d.toLocaleDateString("en-AU", { weekday: "long" });
  const month = d.toLocaleDateString("en-AU", { month: "long" });
  return `${weekday} ${day}${suffix} ${month}`;
}

export default function OrderForm() {
  const {
    open,
    setOpen,
    preselectedPack,
    setPreselectedPack,
    inventory,
    refreshInventory,
  } = useOrder();

  const [selectedId, setSelectedId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [form, setForm] = useState({ name: "", instagram: "", phone: "", pickup_time: "" });

  // Default selection = first in-stock pack, unless one was preselected
  useEffect(() => {
    if (!open) return;
    refreshInventory();
    const available = (inventory.items || []).filter((p) => !p.sold_out);
    if (preselectedPack && available.find((p) => p.id === preselectedPack.id)) {
      setSelectedId(preselectedPack.id);
    } else if (available.length > 0) {
      setSelectedId(available[0].id);
    } else {
      setSelectedId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, preselectedPack]);

  // Reset on close
  useEffect(() => {
    if (!open) {
      const t = setTimeout(() => {
        setDone(null);
        setPreselectedPack(null);
        setForm({ name: "", instagram: "", phone: "", pickup_time: "" });
      }, 350);
      return () => clearTimeout(t);
    }
  }, [open, setPreselectedPack]);

  const selectedPack = useMemo(
    () => (inventory.items || []).find((p) => p.id === selectedId) || null,
    [inventory.items, selectedId]
  );

  const allSoldOut = inventory.all_sold_out;
  const total = selectedPack ? selectedPack.price : 0;

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (allSoldOut) return toast.error("This drop is sold out");
    if (!selectedPack || selectedPack.sold_out) return toast.error("Please select an available pack");
    if (!form.name.trim()) return toast.error("Name is required");
    if (!form.instagram.trim()) return toast.error("Instagram handle is required");
    if (!form.phone.trim()) return toast.error("Contact number is required");
    if (!form.pickup_time) return toast.error("Please pick a pickup time");

    const ig = form.instagram.trim().replace(/^@+/, "");

    const item = {
      pack_id: selectedPack.id,
      pack_name: selectedPack.name,
      pack_size: selectedPack.size,
      unit_price: selectedPack.price,
      quantity: 1,
    };

    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/orders`, {
        items: [item],
        fulfillment: "pickup",
        customer: {
          name: form.name.trim(),
          phone: form.phone.trim(),
          instagram: `@${ig}`,
          pickup_time: form.pickup_time,
        },
      });
      setDone(data);
      toast.success("Pre-order locked in!", {
        description: `Ref #${data.id.slice(0, 8).toUpperCase()}`,
      });
      refreshInventory();
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      toast.error("Could not place pre-order", { description: detail });
      refreshInventory();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        data-testid="order-form-dialog"
        className="bg-[#0A0D26] text-[#F6F0E8] border border-[#F6F0E8]/10 rounded-3xl w-full max-w-lg p-0 max-h-[90vh] overflow-hidden flex flex-col"
      >
        {done ? (
          <ConfirmationView done={done} onClose={() => setOpen(false)} />
        ) : (
          <>
            <DialogHeader className="px-6 pt-6 pb-3 border-b border-[#F6F0E8]/10 shrink-0">
              <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-[#F4A623]">
                <Flame size={14} /> Lock in your pre-order
              </div>
              <DialogTitle className="font-display text-3xl uppercase text-[#F6F0E8]">
                Pre-order Nibs
              </DialogTitle>
              <DialogDescription className="text-[#F6F0E8]/65 text-sm">
                Limited drop. Pick your pack — we'll follow up via Instagram.
              </DialogDescription>
            </DialogHeader>

            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
              {allSoldOut ? (
                <div
                  data-testid="order-form-sold-out"
                  className="rounded-2xl border border-[#ED0B09]/40 bg-[#ED0B09]/10 p-5 flex items-start gap-3"
                >
                  <AlertTriangle className="text-[#ED0B09] shrink-0" />
                  <div>
                    <div className="font-display text-xl uppercase">Drop sold out</div>
                    <div className="text-sm text-[#F6F0E8]/75 mt-1">
                      We're cooked through. Follow @gullwaynibs on Instagram for the next drop announcement.
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-2" data-testid="order-form-pack-list">
                  {(inventory.items || []).map((p) => {
                    const active = p.id === selectedId;
                    const soldOut = p.sold_out;
                    return (
                      <button
                        key={p.id}
                        type="button"
                        disabled={soldOut}
                        onClick={() => setSelectedId(p.id)}
                        data-testid={`order-form-pack-${p.id}`}
                        className={`w-full flex items-center justify-between gap-3 px-4 py-3 rounded-2xl border text-left transition-all ${
                          soldOut
                            ? "bg-[#171D4F]/30 border-[#F6F0E8]/10 opacity-50 cursor-not-allowed"
                            : active
                            ? "bg-[#171D4F] border-[#ED0B09] shadow-[0_0_20px_rgba(237,11,9,0.35)]"
                            : "bg-[#171D4F]/40 border-[#F6F0E8]/10 hover:border-[#F4A623]/40"
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <span
                            className={`h-5 w-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                              active && !soldOut ? "border-[#ED0B09] bg-[#ED0B09]" : "border-[#F6F0E8]/30"
                            }`}
                          >
                            {active && !soldOut && <Check size={12} className="text-[#F6F0E8]" />}
                          </span>
                          <div>
                            <div className="font-display text-lg uppercase text-[#F6F0E8] leading-none">
                              {p.name}
                            </div>
                            <div className="text-[11px] uppercase tracking-widest text-[#F6F0E8]/55 mt-1 flex items-center gap-2">
                              <span>{p.size} nibs · {p.tag}</span>
                              <span
                                className={`${
                                  soldOut
                                    ? "text-[#ED0B09]"
                                    : p.remaining <= Math.max(2, Math.ceil(p.limit * 0.2))
                                    ? "text-[#F4A623]"
                                    : "text-emerald-300"
                                }`}
                                data-testid={`order-form-stock-${p.id}`}
                              >
                                · {soldOut ? "sold out" : `${p.remaining} left`}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className={`font-display text-2xl ${soldOut ? "text-[#F6F0E8]/40 line-through" : "text-[#F4A623]"}`}>
                          ${p.price}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}

              {!allSoldOut && (
                <div className="space-y-3 pt-2">
                  <div>
                    <Label className="text-xs uppercase tracking-[0.25em] text-[#F4A623]">
                      Pickup Time <span className="text-[#ED0B09]">*</span>
                    </Label>
                    <div className="mt-2 grid grid-cols-3 sm:grid-cols-4 gap-1.5" data-testid="order-form-pickup-times">
                      {(inventory.time_slots || []).map((s) => {
                        const active = form.pickup_time === s.id;
                        const full = s.sold_out;
                        return (
                          <button
                            key={s.id}
                            type="button"
                            disabled={full}
                            onClick={() => update("pickup_time", s.id)}
                            data-testid={`order-form-pickup-time-${s.id}`}
                            className={`px-2 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider transition-all border text-center ${
                              full
                                ? "bg-[#171D4F]/30 border-[#F6F0E8]/10 text-[#F6F0E8]/35 line-through cursor-not-allowed"
                                : active
                                ? "bg-[#ED0B09] border-[#ED0B09] text-[#F6F0E8] shadow-[0_0_18px_rgba(237,11,9,0.45)]"
                                : "bg-[#171D4F]/60 border-[#F6F0E8]/15 text-[#F6F0E8] hover:border-[#F4A623]/50"
                            }`}
                          >
                            <div>{s.label}</div>
                            <div className={`text-[9px] mt-0.5 ${full ? "text-[#ED0B09]" : active ? "text-[#F6F0E8]/85" : "text-[#F6F0E8]/55"}`}>
                              {full ? "Full" : `${s.remaining} left`}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <Field label="Name" required>
                    <Input
                      data-testid="order-form-name"
                      value={form.name}
                      onChange={(e) => update("name", e.target.value)}
                      placeholder="Your name"
                      className="bg-[#171D4F] border-[#F6F0E8]/15 text-[#F6F0E8] placeholder:text-[#F6F0E8]/40 focus-visible:ring-[#ED0B09]"
                    />
                  </Field>
                  <Field label="Instagram Handle" required>
                    <Input
                      data-testid="order-form-instagram"
                      value={form.instagram}
                      onChange={(e) => update("instagram", e.target.value)}
                      placeholder="@yourhandle"
                      className="bg-[#171D4F] border-[#F6F0E8]/15 text-[#F6F0E8] placeholder:text-[#F6F0E8]/40 focus-visible:ring-[#ED0B09]"
                    />
                  </Field>
                  <Field label="Contact Number" required>
                    <Input
                      data-testid="order-form-phone"
                      type="tel"
                      inputMode="tel"
                      value={form.phone}
                      onChange={(e) => update("phone", e.target.value)}
                      placeholder="04XX XXX XXX"
                      className="bg-[#171D4F] border-[#F6F0E8]/15 text-[#F6F0E8] placeholder:text-[#F6F0E8]/40 focus-visible:ring-[#ED0B09]"
                    />
                  </Field>
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-[#F6F0E8]/10 bg-[#0A0D26] shrink-0">
              {!allSoldOut && (
                <div className="flex items-baseline justify-between mb-3">
                  <span className="text-xs uppercase tracking-[0.25em] text-[#F6F0E8]/60" data-testid="order-form-summary">
                    {selectedPack ? `${selectedPack.size} nibs` : "Choose a pack"}
                  </span>
                  <span className="font-display text-2xl text-[#F4A623]" data-testid="order-form-total">
                    ${total.toFixed(2)}
                  </span>
                </div>
              )}
              <button
                type="button"
                onClick={allSoldOut ? () => setOpen(false) : submit}
                disabled={submitting || (!allSoldOut && !selectedPack)}
                data-testid="order-form-submit"
                className={`w-full inline-flex items-center justify-center gap-2 font-bold uppercase tracking-[0.18em] py-3.5 rounded-full transition-all ${
                  allSoldOut
                    ? "bg-[#171D4F] text-[#F6F0E8] border border-[#F6F0E8]/15"
                    : "bg-[#ED0B09] hover:bg-[#B42A27] disabled:opacity-60 text-[#F6F0E8] shadow-[0_0_28px_rgba(237,11,9,0.55)]"
                }`}
              >
                {submitting ? <Loader2 className="animate-spin" size={16} /> : <Flame size={16} />}
                {allSoldOut ? "Close" : submitting ? "Sending..." : "Place Pre-order"}
              </button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ConfirmationView({ done, onClose }) {
  const firstItem = done.items[0];
  const reference = `${done.customer?.name || "Your Name"} - ${firstItem.pack_name}`;
  const payId = "gullwaynibs@gmail.com";
  const pt = done.customer?.pickup_time;
  let pickupLabel = pt;
  if (pt && /^\d{2}:\d{2}$/.test(pt)) {
    const h = parseInt(pt.slice(0, 2), 10);
    pickupLabel = `${h % 12 || 12}:${pt.slice(3)} ${h < 12 ? "AM" : "PM"}`;
  }

  const copy = async (value, label) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copied`);
    } catch (e) {
      toast.error("Copy failed — long-press to copy");
    }
  };

  return (
    <div className="px-6 py-8 text-center overflow-y-auto" data-testid="order-form-confirmation">
      <div className="mx-auto h-16 w-16 rounded-full bg-emerald-500 flex items-center justify-center shadow-[0_0_30px_rgba(16,185,129,0.45)]">
        <Check size={28} className="text-[#F6F0E8]" />
      </div>
      <h3 className="mt-5 font-display text-3xl uppercase text-[#F6F0E8] leading-tight">
        Your order has been received
      </h3>
      <p className="mt-2 text-[#F6F0E8]/70 text-sm">
        Ref <span className="text-[#F4A623] font-bold">#{done.id.slice(0, 8).toUpperCase()}</span> · Thank you {done.customer?.name?.split(" ")[0] || ""}!
      </p>

      <div className="mt-5 bg-[#171D4F]/70 border border-[#F6F0E8]/10 rounded-2xl p-4 text-left text-sm space-y-3">
        <div className="text-xs uppercase tracking-[0.25em] text-[#F4A623] flex items-center gap-2">
          <Flame size={12} /> Payment Details
        </div>

        <div>
          <div className="text-[11px] uppercase tracking-widest text-[#F6F0E8]/55">PayID</div>
          <button
            type="button"
            onClick={() => copy(payId, "PayID")}
            data-testid="confirm-copy-payid"
            className="mt-1 w-full flex items-center justify-between gap-2 bg-[#0A0D26] border border-[#F6F0E8]/15 rounded-xl px-3 py-2 hover:border-[#F4A623]/50 transition-colors"
          >
            <span className="font-display text-xl text-[#F6F0E8]">{payId}</span>
            <Copy size={14} className="text-[#F4A623]" />
          </button>
        </div>

        <div>
          <div className="text-[11px] uppercase tracking-widest text-[#F6F0E8]/55">Reference</div>
          <button
            type="button"
            onClick={() => copy(reference, "Reference")}
            data-testid="confirm-copy-reference"
            className="mt-1 w-full flex items-center justify-between gap-2 bg-[#0A0D26] border border-[#F6F0E8]/15 rounded-xl px-3 py-2 hover:border-[#F4A623]/50 transition-colors text-left"
          >
            <span className="text-[#F6F0E8] truncate">{reference}</span>
            <Copy size={14} className="text-[#F4A623] shrink-0" />
          </button>
        </div>

        <div className="border-t border-[#F6F0E8]/10 pt-3 flex justify-between">
          <span className="text-[#F6F0E8]/70">Amount</span>
          <span className="text-[#F4A623] font-bold font-display text-xl">${done.subtotal.toFixed(2)}</span>
        </div>
        {pickupLabel && (
          <div className="flex justify-between">
            <span className="text-[#F6F0E8]/70">Pickup Time</span>
            <span className="text-[#F6F0E8]">{pickupLabel}</span>
          </div>
        )}
      </div>

      <p className="mt-4 text-[#F6F0E8]/80 text-sm leading-relaxed">
        Please send a <span className="text-[#F4A623] font-semibold">screenshot of payment</span> to our
        {" "}
        <a
          href="https://instagram.com/gullwaynibs"
          target="_blank"
          rel="noreferrer"
          data-testid="confirm-instagram-link"
          className="text-[#F4A623] font-semibold underline-offset-2 hover:underline"
        >
          Instagram page
        </a>{" "}
        to confirm your order.
      </p>

      <div className="mt-4 bg-[#ED0B09]/10 border border-[#ED0B09]/40 rounded-2xl p-3.5 text-left flex gap-3">
        <AlertCircle size={18} className="text-[#ED0B09] shrink-0 mt-0.5" />
        <p className="text-[#F6F0E8] text-sm leading-relaxed">
          <span className="font-bold uppercase text-[#ED0B09] tracking-widest text-xs block mb-1">Important</span>
          Payment must be made by <span className="font-bold text-[#F4A623]">{formatPaymentDue(getNextPaymentDue())}</span> to confirm your order.
          Unpaid orders after this date may be released due to limited availability.
        </p>
      </div>

      <p className="mt-4 text-[#F6F0E8]/75 text-sm">
        Thank you for your support <span className="text-[#F6F0E8]">🤍</span>
      </p>

      <div className="mt-4 inline-flex items-center gap-2 text-[11px] uppercase tracking-widest text-[#F6F0E8]/55">
        <Instagram size={12} className="text-[#F4A623]" /> We'll DM you once payment lands
      </div>

      <button
        type="button"
        onClick={onClose}
        data-testid="order-form-confirmation-close"
        className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-[#ED0B09] hover:bg-[#B42A27] text-[#F6F0E8] font-bold uppercase tracking-[0.18em] py-3 rounded-full transition-all shadow-[0_0_20px_rgba(237,11,9,0.5)]"
      >
        Done
      </button>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-[0.25em] text-[#F4A623]">
        {label} {required && <span className="text-[#ED0B09]">*</span>}
      </Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
